import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuIcon from "@mui/icons-material/Menu";
import { alpha, Box, Checkbox, CSSObject, Divider, Drawer, List, ListItem, ListSubheader, styled, Theme, Tooltip } from "@mui/material";
import React, { useContext, useEffect, useMemo } from "react";
import IconComponent from "../../common/components/IconComponent";
import { MenuItemDesc, MenuSectionDesc } from "../../containers/Navigation";
import BundleContext from "../../UserInterfaceModule/Contexts/BundleContext";
import useLocationHistory from "../../UserInterfaceModule/Hooks/useLocationHistory";
import AuthContext from "../AuthContext";
import MenuListItem from "./MenuListItem";
import MenuListItemAvatar from "./MenuListItemAvatar";

const DRAWER_WIDTH = 240;
const LEFT_MENU_OPEN_STATE_KEY = "leftMenu.open";
const LEFT_MENU_HOVER_ENABLED_KEY = "leftMenu.hoverEnabled";

const openedMixin = (theme: Theme): CSSObject => ({
  width: DRAWER_WIDTH,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  ...theme.mixins.toolbar,
}));

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "isOverlay",
})<{ open?: boolean; isOverlay?: boolean }>(({ theme }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  "& .MuiDrawer-paper": {
    boxShadow: theme.shadows[3],
    backgroundColor: theme.palette.mode === "light" ? alpha(theme.palette.secondary.main, 0.01) : "#3d3935",
    borderRight: theme.palette.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : undefined,
  },
  variants: [
    {
      props: ({ open, isOverlay }) => open && !isOverlay,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      },
    },
    {
      props: ({ open, isOverlay }) => !open || isOverlay,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
    {
      props: ({ isOverlay }) => isOverlay,
      style: {
        "& .MuiDrawer-paper": {
          position: "fixed",
          zIndex: theme.zIndex.drawer + 1,
          width: DRAWER_WIDTH,
        },
      },
    },
  ],
}));

interface LeftMenuProps {
  onToggle?: () => void;
}

export default function LeftMenu({ onToggle }: LeftMenuProps) {
  const [open, setOpen] = React.useState(() => {
    const savedState = sessionStorage.getItem(LEFT_MENU_OPEN_STATE_KEY);
    return savedState ? JSON.parse(savedState) : false;
  });
  const [hoverEnabled, setHoverEnabled] = React.useState(() => {
    const savedState = sessionStorage.getItem(LEFT_MENU_HOVER_ENABLED_KEY);
    return savedState ? JSON.parse(savedState) : true;
  });
  const [isHovering, setIsHovering] = React.useState(false);
  const auth = useContext(AuthContext);
  const bundle = useContext(BundleContext);
  const { navigate } = useLocationHistory();

  useEffect(() => {
    sessionStorage.setItem(LEFT_MENU_OPEN_STATE_KEY, JSON.stringify(open));
  }, [open]);

  useEffect(() => {
    sessionStorage.setItem(LEFT_MENU_HOVER_ENABLED_KEY, JSON.stringify(hoverEnabled));
  }, [hoverEnabled]);

  const isDrawerOpen = open || (hoverEnabled && isHovering);
  const isOverlayMode = !open && hoverEnabled && isHovering;

  const handleMouseEnter = () => {
    if (hoverEnabled) {
      setIsHovering(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  const handleHoverToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHoverEnabled(event.target.checked);
  };

  const dynamicMenuItems = useMemo<MenuItemDesc[]>(() => {
    const items: MenuItemDesc[] = bundle.Views.filter((view) => view.Menu && view.Menu.Visible !== false).map((view) => ({
      title: view.Menu!.Title,
      description: view.Menu?.Description,
      sectionTitle: view.Menu?.SectionTitle || "Main",
      icon: <IconComponent iconName={view.Menu?.IconName} />,
      path: view.RoutePath,
      requiredAuth: view.RequiresAuthentication,
    }));
    return items;
  }, [bundle.Views]);

  const menuItems: MenuSectionDesc[] = useMemo(() => {
    const menuItemsBySection = dynamicMenuItems.reduce<Record<string, MenuItemDesc[]>>((acc, item) => {
      const sectionTitle = item.sectionTitle;
      if (!acc[sectionTitle]) {
        acc[sectionTitle] = [];
      }
      acc[sectionTitle].push(item);
      return acc;
    }, {});
    const sections: MenuSectionDesc[] = Object.entries(menuItemsBySection).map(([title, items]) => ({
      title,
      items,
    }));
    return sections;
  }, [dynamicMenuItems]);

  const handleDrawerToggle = () => {
    setOpen(!open);
    if (onToggle) {
      onToggle();
    }
  };

  const handleOnClick = (item: MenuItemDesc) => {
    if (item.path) {
      navigate(item.path, item.title, true);
    }
  };

  const sections = useMemo<MenuSectionDesc[]>(
    () =>
      menuItems
        .map((section) => {
          const items = section.items.filter((item) => item.requiredAuth === false || item.requiredAuth === auth.isAuthenticated);
          return {
            ...section,
            items: items,
          };
        })
        .filter((section) => section.items.length > 0),
    [auth.isAuthenticated, menuItems]
  );

  return (
    <StyledDrawer variant="permanent" open={isDrawerOpen} isOverlay={isOverlayMode} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <DrawerHeader>
        <List sx={{ width: "100%", p: 0 }}>
          {!open ? (
            <MenuListItem open={open} icon={<MenuIcon />} primaryText="Open Menu" onClick={handleDrawerToggle} showText={false} />
          ) : (
            <MenuListItem open={open} rightIcon={<ChevronLeftIcon />} primaryText="Close Menu" onClick={handleDrawerToggle} showText={false} />
          )}
        </List>
      </DrawerHeader>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        {sections.map((section, index) => (
          <React.Fragment key={index}>
            <Divider />
            <List
              subheader={
                isDrawerOpen && section.title ? (
                  <ListSubheader
                    component="div"
                    sx={{
                      backgroundColor: "transparent",
                      color: "text.secondary",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      py: 1,
                      px: 2,
                    }}
                  >
                    {section.title}
                  </ListSubheader>
                ) : undefined
              }
            >
              {section.items.map((item, index) => (
                <MenuListItem
                  key={index}
                  open={isDrawerOpen}
                  icon={item.icon}
                  primaryText={item.title}
                  secondaryText={item.description}
                  onClick={() => handleOnClick(item)}
                />
              ))}
            </List>
          </React.Fragment>
        ))}

        <Box sx={{ flexGrow: 1 }} />

        <Divider />
        <List>
          {!open && (
            <ListItem sx={{ justifyContent: "center", py: 0.5 }}>
              <Tooltip title="Enable hover to open" placement="right">
                <Checkbox checked={hoverEnabled} onChange={handleHoverToggle} size="small" sx={{ p: 0.5 }} />
              </Tooltip>
            </ListItem>
          )}
          <MenuListItemAvatar open={isDrawerOpen} additionalSx={{ pb: 1 }} />
        </List>
      </Box>
    </StyledDrawer>
  );
}
